// AI Assistant Context - Global state management for AI chat functionality
// Provides context-aware AI assistance throughout the Pro IRP platform

import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const AIContext = createContext();

const API_BASE = process.env.REACT_APP_API_URL || '';

// Page context mapping from URL paths
function getPageContext(pathname) {
  if (pathname.startsWith('/clients/') && pathname !== '/clients/import') return 'ClientProfile';
  if (pathname === '/clients' || pathname === '/clients/import') return 'Clients';
  if (pathname === '/dashboard' || pathname === '/') return 'Dashboard';
  if (pathname === '/tasks') return 'Tasks';
  if (pathname === '/calendar') return 'Calendar';
  if (pathname === '/aep-wizard') return 'AEPWizard';
  if (pathname === '/oep') return 'OEPHub';
  if (pathname === '/automations') return 'Automations';
  if (pathname === '/settings') return 'Settings';
  if (pathname === '/add-client') return 'AddClient';
  if (pathname.startsWith('/founder')) return 'FounderDashboard';
  return 'Dashboard';
}

// Context-specific welcome messages
const WELCOME_MESSAGES = {
  Dashboard: "Welcome! I can help you understand your dashboard, prioritize clients, or explain any metrics you see.",
  ClientProfile: "I can help you understand this client's risk score, suggest outreach strategies, or explain their data.",
  Clients: "Need help finding clients, importing data, or understanding filters? I'm here to help!",
  Tasks: "I can help you manage tasks, understand priorities, or create new tasks.",
  Calendar: "Questions about scheduling, calendar sync, or appointments? Ask away!",
  AEPWizard: "AEP is crucial! I can explain the timeline, help with templates, or guide you through automations.",
  OEPHub: "OEP is all about retention. Let me help you with your cohort or outreach strategy.",
  Automations: "Need to set up automations or understand workflows? I'll walk you through it.",
  Settings: "I can help you configure settings, security, or integrations.",
  AddClient: "Adding a new client? I can explain the fields or help with data entry.",
  FounderDashboard: "I can explain any metrics, adoption rates, or system health indicators you see."
};

// Suggested prompts by page context
const CONTEXT_SUGGESTIONS = {
  Dashboard: [
    "What should I focus on today?",
    "Explain my risk distribution",
    "How do I use the priority call queue?",
    "What does my retention rate mean?"
  ],
  ClientProfile: [
    "How can I reduce this client's risk score?",
    "What should I do about their risk factors?",
    "How do I connect Blue Button for this client?",
    "Create a follow-up task for this client"
  ],
  Clients: [
    "How do I filter at-risk clients?",
    "How do I import clients from CSV?",
    "Explain the risk score categories",
    "How do I export my client list?"
  ],
  Tasks: [
    "How do I prioritize my tasks?",
    "Create a task for me",
    "Show me how to filter overdue tasks",
    "How do I link a task to a client?"
  ],
  Calendar: [
    "How do I connect Google Calendar?",
    "How do I schedule a client meeting?",
    "Why aren't my events syncing?",
    "How do I add a new appointment?"
  ],
  AEPWizard: [
    "When does AEP start and end?",
    "How do I set up AEP automations?",
    "What templates should I use?",
    "Explain the countdown contacts"
  ],
  OEPHub: [
    "What is OEP and who is eligible?",
    "How do I identify at-risk OEP clients?",
    "Set up OEP retention campaigns",
    "What's the OEP cohort?"
  ],
  Automations: [
    "What automations are available?",
    "How do birthday automations work?",
    "Create a custom workflow",
    "How do I test an automation?"
  ],
  Settings: [
    "How do I enable two-factor authentication?",
    "How do I change my assistant's name?",
    "How do I connect my CRM?",
    "How do I export my data?"
  ]
};

export const AIProvider = ({ children }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [contextData, setContextData] = useState({});
  const [usage, setUsage] = useState({ messagesUsed: 0, messagesLimit: 100 });
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const abortControllerRef = useRef(null);

  // Get current page context
  const pageContext = getPageContext(location.pathname);

  // Get suggestions for current page
  const contextSuggestions = CONTEXT_SUGGESTIONS[pageContext] || CONTEXT_SUGGESTIONS.Dashboard;

  // Get welcome message for current page
  const welcomeMessage = WELCOME_MESSAGES[pageContext] || WELCOME_MESSAGES.Dashboard;

  // Extract context data from URL (e.g., client ID)
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'clients' && pathParts[2] && pathParts[2] !== 'import') {
      setContextData({ clientId: pathParts[2] });
    } else {
      setContextData({});
    }
  }, [location.pathname]);

  // Load usage stats when opened
  useEffect(() => {
    if (isOpen) {
      loadUsage();
    }
  }, [isOpen]);

  // Load usage stats
  const loadUsage = async () => {
    try {
      const res = await fetch(`${API_BASE}/ai/usage`, { credentials: 'include' });
      const data = await res.json();
      if (data.ok) {
        setUsage({
          messagesUsed: data.messagesUsed,
          messagesLimit: data.messagesLimit
        });
      }
    } catch (err) {
      console.error('[AI] Failed to load usage:', err);
    }
  };

  // Load conversation history
  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_BASE}/ai/history?limit=10`, { credentials: 'include' });
      const data = await res.json();
      if (data.ok) {
        setHistory(data.conversations || []);
      }
    } catch (err) {
      console.error('[AI] Failed to load history:', err);
    }
  };

  // Load a previous conversation
  const loadConversation = async (convId) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/ai/conversation/${convId}`, { credentials: 'include' });
      const data = await res.json();
      if (data.ok) {
        setConversationId(convId);
        setMessages(data.messages.map((m, idx) => ({
          id: `hist-${idx}`,
          role: m.role,
          content: m.content,
          timestamp: m.created_at
        })));
        setShowHistory(false);
      }
    } catch (err) {
      console.error('[AI] Failed to load conversation:', err);
      setError('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  // Send message with streaming support
  const sendMessage = useCallback(async (message) => {
    if (!message.trim() || loading || isStreaming) return;

    // Add user message immediately
    const msgId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const userMessage = {
      id: msgId,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setError(null);

    // Try streaming first
    try {
      setIsStreaming(true);
      setStreamingContent('');

      // Add placeholder for assistant message
      const assistantMsgId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setMessages(prev => [...prev, {
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        isStreaming: true,
        timestamp: new Date().toISOString()
      }]);

      const response = await fetch(`${API_BASE}/ai/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          conversationId,
          pageContext,
          contextData
        })
      });

      if (!response.ok) {
        throw new Error('Stream request failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let finalData = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'chunk') {
                fullContent += data.content;
                setMessages(prev => prev.map(m =>
                  m.id === assistantMsgId
                    ? { ...m, content: fullContent }
                    : m
                ));
              } else if (data.type === 'done') {
                finalData = data;
              } else if (data.type === 'error') {
                throw new Error(data.error);
              }
            } catch (parseErr) {
              console.error('AI stream parse error:', parseErr, 'chunk:', line);
            }
          }
        }
      }

      // Finalize the message
      setMessages(prev => prev.map(m =>
        m.id === assistantMsgId
          ? {
              ...m,
              content: fullContent,
              isStreaming: false,
              suggestedActions: finalData?.suggestedActions || []
            }
          : m
      ));

      if (finalData?.conversationId) {
        setConversationId(finalData.conversationId);
      }

      loadUsage();

    } catch (streamErr) {
      console.error('[AI] Stream error, falling back to regular:', streamErr);

      // Fall back to non-streaming
      try {
        setLoading(true);
        setIsStreaming(false);

        // Remove the streaming placeholder
        setMessages(prev => prev.filter(m => !m.isStreaming));

        const res = await fetch(`${API_BASE}/ai/chat`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message,
            conversationId,
            pageContext,
            contextData
          })
        });

        const data = await res.json();

        if (data.ok) {
          setConversationId(data.conversationId);
          setMessages(prev => [...prev, {
            id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            role: 'assistant',
            content: data.message,
            suggestedActions: data.suggestedActions,
            timestamp: new Date().toISOString()
          }]);
          loadUsage();
        } else {
          setError(data.error);
          if (data.limitReached) {
            setMessages(prev => [...prev, {
              id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              role: 'system',
              content: 'Daily message limit reached. Your limit resets at midnight.',
              timestamp: new Date().toISOString()
            }]);
          }
        }
      } catch (fallbackErr) {
        console.error('[AI] Fallback error:', fallbackErr);
        setError('Failed to send message. Please try again.');
      } finally {
        setLoading(false);
      }
    } finally {
      setIsStreaming(false);
    }
  }, [conversationId, pageContext, contextData, loading, isStreaming]);

  // Start a new conversation
  const startNewConversation = useCallback(() => {
    setConversationId(null);
    setMessages([]);
    setError(null);
    setShowHistory(false);
  }, []);

  // Toggle chat open/closed
  const toggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
    setIsMinimized(false);
    if (!isOpen) {
      loadUsage();
      loadHistory();
    }
  }, [isOpen]);

  // Minimize chat
  const minimize = useCallback(() => {
    setIsMinimized(true);
  }, []);

  // Expand from minimized
  const expand = useCallback(() => {
    setIsMinimized(false);
  }, []);

  // Execute a suggested action
  const executeAction = useCallback((action) => {
    if (action.type === 'navigate' && action.path) {
      window.location.href = action.path;
    } else if (action.type === 'action' && action.action) {
      window.dispatchEvent(new CustomEvent('ai-action', { detail: action }));
    }
  }, []);

  // Submit feedback
  const submitFeedback = useCallback(async (rating, text) => {
    if (!conversationId) return;

    try {
      await fetch(`${API_BASE}/ai/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversationId, rating, text })
      });
    } catch (err) {
      console.error('[AI] Feedback error:', err);
    }
  }, [conversationId]);

  // Toggle history panel
  const toggleHistory = useCallback(() => {
    setShowHistory(prev => !prev);
    if (!showHistory) {
      loadHistory();
    }
  }, [showHistory]);

  return (
    <AIContext.Provider value={{
      // State
      isOpen,
      isMinimized,
      messages,
      conversationId,
      loading,
      error,
      isStreaming,
      suggestions: contextSuggestions,
      pageContext,
      contextData,
      usage,
      history,
      showHistory,
      welcomeMessage,

      // Actions
      sendMessage,
      startNewConversation,
      toggleOpen,
      minimize,
      expand,
      executeAction,
      submitFeedback,
      setContextData,
      toggleHistory,
      loadConversation
    }}>
      {children}
    </AIContext.Provider>
  );
};

export const useAI = () => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
};

export default AIContext;
